import { useState } from "react"
import { Play, Download, FileText } from "lucide-react"
import YouTube from "react-youtube"
import { Button } from "../ui/button"
import Card, { CardContent, CardHeader, CardTitle } from "../Card"

export default function VideoBlock({ block, hideTitle = false, isConcatenated = false }) {
  const { content } = block || {}

  // Vérifier si le contenu existe
  if (!content) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p className="text-gray-500">Contenu vidéo non disponible.</p>
        </CardContent>
      </Card>
    )
  }

  // Fonction pour extraire l'ID YouTube d'une URL
  const extractYouTubeId = (url) => {
    if (!url) return null
    
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/
    const match = url.match(regExp)
    return (match && match[7].length === 11) ? match[7] : null
  }

  // Vérifier si l'URL est une vidéo YouTube
  const isYouTubeVideo = (url) => {
    return extractYouTubeId(url) !== null
  }

  // Options pour le lecteur YouTube
  const youtubeOpts = {
    height: '100%',
    width: '100%',
    playerVars: {
      autoplay: 0,
      controls: 1,
      rel: 0,
      showinfo: 0,
      modestbranding: 1,
    },
  }

  const videoUrl = content?.videoUrl
  const youtubeId = extractYouTubeId(videoUrl)
  const isYoutube = isYouTubeVideo(videoUrl)

  return (
    <div className="space-y-6">
      {/* Video Player */}
      {isConcatenated ? (
        // Rendu fluide sans Card pour les blocs concaténés
        <div className="my-8">
          <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
            {isYoutube && youtubeId ? (
              /* YouTube Player */
              <div className="w-full h-full">
                <YouTube
                  videoId={youtubeId}
                  opts={youtubeOpts}
                  className="w-full h-full"
                  iframeClassName="w-full h-full rounded-lg"
                />
              </div>
            ) : videoUrl ? (
              /* Lecteur vidéo HTML5 pour les autres formats */
              <video
                className="w-full h-full object-cover"
                controls
                preload="metadata"
              >
                <source src={videoUrl} type="video/mp4" />
                <source src={videoUrl} type="video/webm" />
                <source src={videoUrl} type="video/ogg" />
                Votre navigateur ne supporte pas la lecture vidéo.
              </video>
            ) : (
              /* Placeholder si pas d'URL vidéo */
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
                <div className="text-center text-white">
                  <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Play className="w-8 h-8 ml-1" />
                  </div>
                  <p className="text-lg font-medium">{block.title}</p>
                  <p className="text-gray-400 text-xs mt-2">
                    Aucune vidéo disponible
                  </p>
                </div>
              </div>
            )}
            
          </div>
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
              {isYoutube && youtubeId ? (
                /* YouTube Player */
                <div className="w-full h-full">
                  <YouTube
                    videoId={youtubeId}
                    opts={youtubeOpts}
                    className="w-full h-full"
                    iframeClassName="w-full h-full rounded-lg"
                  />
                </div>
              ) : videoUrl ? (
                /* Lecteur vidéo HTML5 pour les autres formats */
                <video
                  className="w-full h-full object-cover"
                  controls
                  preload="metadata"
                >
                  <source src={videoUrl} type="video/mp4" />
                  <source src={videoUrl} type="video/webm" />
                  <source src={videoUrl} type="video/ogg" />
                  Votre navigateur ne supporte pas la lecture vidéo.
                </video>
              ) : (
                /* Placeholder si pas d'URL vidéo */
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
                  <div className="text-center text-white">
                    <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Play className="w-8 h-8 ml-1" />
                    </div>
                    <p className="text-lg font-medium">{block.title}</p>
                    <p className="text-gray-400 text-xs mt-2">
                      Aucune vidéo disponible
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Video Description */}
      {block.description && (
        <Card>
          <CardHeader>
            <CardTitle>Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">{block.description}</p>
          </CardContent>
        </Card>
      )}

      {/* Resources */}
      {content.resources && content.resources.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5" />
              Ressources
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {content.resources.map((resource, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-primary" />
                    <span className="font-medium">Ressource {index + 1}</span>
                  </div>
                  <Button size="sm" variant="outline" asChild>
                    <a href={resource} target="_blank" rel="noopener noreferrer" className="flex items-center">
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger
                    </a>
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transcript */}
      {content.transcript && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Transcription
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700 leading-relaxed">{content.transcript}</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
